import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Compass,
  Timer,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  Lock,
  Unlock,
  Clock,
  Lightbulb,
  Eye,
  Check,
  X,
  Sparkles,
  Gauge,
} from 'lucide-react';
import { lldProblemService, LLDProblem } from '../../../services/lldProblemService';

type Mode = 'learn' | 'practice' | 'interview';
type TimeLimit = 30 | 45 | 60;

interface ModeDef {
  id: Mode;
  title: string;
  tagline: string;
  icon: React.ElementType;
  guidanceLevel: string;
  bestFor: string;
  duration: string;
  features: { icon: React.ElementType; text: string }[];
  recommended?: boolean;
}

const MODES: ModeDef[] = [
  {
    id: 'learn',
    title: 'Learn',
    tagline: 'Guided, step by step',
    icon: BookOpen,
    guidanceLevel: 'Full guidance',
    bestFor: 'First time with this problem',
    duration: '~45–60 min',
    features: [
      { icon: Lock, text: 'Fixed step order — unlock as you go' },
      { icon: Lightbulb, text: 'Hints visible on every step' },
      { icon: Eye, text: 'Reference solution after each step' },
    ],
  },
  {
    id: 'practice',
    title: 'Practice',
    tagline: 'Free flow, help on tap',
    icon: Compass,
    guidanceLevel: 'On-demand guidance',
    bestFor: 'Reinforcing what you learned',
    duration: '~40–50 min',
    features: [
      { icon: Unlock, text: 'Jump between sections freely' },
      { icon: Lightbulb, text: 'Up to 3 hints per section, on request' },
      { icon: Eye, text: 'Reveal the solution whenever you want' },
    ],
    recommended: true,
  },
  {
    id: 'interview',
    title: 'Interview',
    tagline: 'Timed. No safety net.',
    icon: Timer,
    guidanceLevel: 'No guidance',
    bestFor: 'Testing yourself under pressure',
    duration: 'You set the clock',
    features: [
      { icon: Clock, text: 'Countdown timer, real interview feel' },
      { icon: X, text: 'No hints, no solutions' },
      { icon: Gauge, text: 'Auto-submit when time runs out' },
    ],
  },
];

const TIME_OPTIONS: { value: TimeLimit; label: string; sub: string }[] = [
  { value: 30, label: '30', sub: 'Sprint' },
  { value: 45, label: '45', sub: 'Standard' },
  { value: 60, label: '60', sub: 'Deep dive' },
];

const difficultyColor = (d?: string): string => {
  switch (d) {
    case 'Beginner': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'Intermediate': return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'Advanced': return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    case 'Expert': return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  }
};

const ModeSelection: React.FC = () => {
  const { problemId } = useParams<{ problemId: string }>();
  const navigate = useNavigate();

  const isScratch = problemId === 'scratch';

  const [problem, setProblem] = useState<LLDProblem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<Mode | null>(null);
  const [timeLimit, setTimeLimit] = useState<TimeLimit>(45);

  useEffect(() => {
    if (isScratch) {
      setLoading(false);
      return;
    }
    fetchProblem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId]);

  const fetchProblem = async () => {
    if (!problemId) {
      setError('Problem ID is missing.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await lldProblemService.getProblemById(problemId);
      setProblem(data);
    } catch {
      setError('Failed to load problem details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = (mode: Mode) => {
    if (mode === 'interview') {
      navigate(`/practice/lld/${problemId}/${mode}?minutes=${timeLimit}`);
    } else {
      navigate(`/practice/lld/${problemId}/${mode}`);
    }
  };

  const handleBackToCatalog = () => navigate('/practice/lld');

  // ─── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="nav-tab-page practice-detail-page mode-selection-page min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="ds-surface-card bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-200 dark:border-gray-700"
        >
          <div className="w-14 h-14 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Workspace Load Failed</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={fetchProblem} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
            <button onClick={handleBackToCatalog} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Catalog
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="nav-tab-page practice-detail-page mode-selection-page min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-[3px] border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400 text-sm">Preparing your session…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lld-page nav-tab-page practice-detail-page mode-selection-page min-h-screen pt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link */}
        <button
          onClick={handleBackToCatalog}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to problems
        </button>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mode-selection-header mb-10">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400">
            {isScratch ? 'Blank session' : 'Choose your mode'}
          </span>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
            {isScratch ? 'Blank session' : problem?.title}
          </h1>
          {problem?.description && !isScratch && (
            <p className="mt-2 text-base text-gray-600 dark:text-gray-400 max-w-2xl">
              {problem.description}
            </p>
          )}
          {isScratch && (
            <p className="mt-2 text-base text-gray-600 dark:text-gray-400 max-w-2xl">
              Open an empty workspace and author your own problem. Type the prompt on Step 1, then work through all eight design steps.
            </p>
          )}
          {!isScratch && (problem?.difficulty || problem?.category) && (
            <div className="mt-3 flex items-center gap-2">
              {problem?.difficulty && (
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${difficultyColor(problem.difficulty)}`}>
                  {problem.difficulty}
                </span>
              )}
              {problem?.category && (
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  {problem.category}
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* ─── Mode cards ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {MODES.map((mode, index) => {
            const Icon = mode.icon;
            const isSelected = selectedMode === mode.id;

            return (
              <motion.div
                key={mode.id}
                className="mode-selection-item h-full"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08, duration: 0.4, ease: 'easeOut' }}
              >
                <div
                  onClick={() => setSelectedMode(mode.id)}
                  className={`mode-selection-card group relative cursor-pointer rounded-xl bg-white dark:bg-gray-800 border shadow-md transition-all duration-300 h-full flex flex-col overflow-hidden ${isSelected ? 'is-selected' : ''}
                    ${isSelected
                      ? 'ring-2 ring-primary-500/50 border-primary-300 dark:border-primary-700 shadow-lg'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-lg hover:-translate-y-0.5'}`}
                >
                  {/* Subtle top accent line */}
                  <div className={`card-accent-strip h-[3px] w-full ${isSelected ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 transition-colors'}`} />

                  <div className="p-6 flex flex-col flex-1">
                    <div className="mode-selection-card__top-row flex items-start justify-between gap-3">
                      {/* Icon — more prominent with a subtle border */}
                      <div className="mode-selection-card__icon w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 flex items-center justify-center mb-5 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
                        <Icon className="w-5.5 h-5.5 text-gray-700 dark:text-gray-200" />
                      </div>
                      {mode.recommended && (
                        <span className="mode-selection-card__popular inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                          <Sparkles className="w-3 h-3" /> Popular
                        </span>
                      )}
                    </div>

                    <div className="mode-selection-card__intro mb-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{mode.title}</h3>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{mode.tagline}</span>
                    </div>

                    <span className="mode-selection-card__guidance inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md mb-4 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 w-fit">
                      {mode.guidanceLevel}
                    </span>

                    {/* Meta — in a subtle card-within-card */}
                    <div className="mode-selection-card__meta flex gap-3 mb-5">
                      <div className="flex-1 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500">Best for</div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-0.5 leading-snug">{mode.bestFor}</div>
                      </div>
                      <div className="w-20 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-500">Time</div>
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-0.5">{mode.duration}</div>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="mode-selection-card__features space-y-2.5 mb-5">
                      {mode.features.map((f, i) => {
                        const FIcon = f.icon;
                        return (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-300">
                            <span className="mode-selection-card__feature-icon mt-0.5 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                              <FIcon className="w-3.5 h-3.5" />
                            </span>
                            <span className="leading-snug">{f.text}</span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Inline expand: interview time picker */}
                    <AnimatePresence initial={false}>
                      {isSelected && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          {mode.id === 'interview' && (
                            <div className="pt-1 pb-4">
                              <div className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-2">
                                Pick your time limit
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                {TIME_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.value}
                                    onClick={(e) => { e.stopPropagation(); setTimeLimit(opt.value); }}
                                    className={`mode-time-option rounded-lg border px-2 py-2 text-center transition-all ${timeLimit === opt.value ? 'is-selected' : ''} ${timeLimit === opt.value
                                      ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-300 dark:ring-primary-700'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'}`}
                                  >
                                    <div className="text-base font-bold text-gray-900 dark:text-white leading-none">{opt.label}<span className="text-[10px] font-normal text-gray-400 ml-0.5">min</span></div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{opt.sub}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* CTA */}
                    <div className="mode-selection-card__action mt-auto pt-4">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStart(mode.id); }}
                        className={`mode-selection-card__cta w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-all bg-primary-600 hover:bg-primary-700 ${isSelected ? 'shadow-lg' : 'opacity-90 group-hover:opacity-100'}`}
                      >
                        {mode.id === 'interview' && isSelected ? `Start ${timeLimit}-min Interview` : `Start ${mode.title}`}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Comparison table ─────────────────────────────────────────── */}
        <div className="mode-comparison-table mt-12 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200/70 dark:border-gray-700/50">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Compare modes at a glance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200/70 dark:border-gray-700/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider w-1/4"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Learn</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Practice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Interview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/70 dark:divide-gray-700/40">
                <tr>
                  <td className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300">Navigation</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Guided &amp; linear</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Free</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Free</td>
                </tr>
                <tr>
                  <td className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300">Hints</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Always shown</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">On request (3)</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">None</td>
                </tr>
                <tr>
                  <td className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300">Reference solution</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">After each step</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">On demand</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">None</td>
                </tr>
                <tr>
                  <td className="px-6 py-3 font-medium text-gray-700 dark:text-gray-300">Timer</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Off</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">Off</td>
                  <td className="px-6 py-3 text-gray-600 dark:text-gray-400">30 / 45 / 60 min</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* New-to-LLD nudge */}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Check className="w-4 h-4 text-emerald-500" />
          New to this problem? <button onClick={() => handleStart('learn')} className="font-medium text-primary-600 dark:text-primary-400 hover:underline">Start with Learn mode</button>
        </div>
      </div>
    </div>
  );
};

export default ModeSelection;
